import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

export interface RenderContext {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
}

/** レンダラ・シーン・カメラ・ライトを初期化する。z 軸を上方向とする。カメラ操作は camera-rig が担う。 */
export const createRenderContext = (canvas: HTMLCanvasElement): RenderContext => {
  // Three.js r163 以降は WebGLRenderer が WebGL2 必須（WebGL1 サポート削除済み）。
  // iOS 14.x など WebGL2 非対応環境では context 作成が失敗するため、事前検査で
  // ユーザー向けの明確なエラーを投げる。index.html のブートストラップ診断が
  // この種のエラーを検知し、全画面の案内モーダルを表示する。
  if (!canvas.getContext('webgl2')) {
    const err = new Error(
      'WebGL2 not supported. iOS 15+/最新 Android Chrome/最新 PC ブラウザが必要です。',
    );
    (err as Error & { code?: string }).code = 'WEBGL2_UNSUPPORTED';
    throw err;
  }
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x0b0e13);

  // 環境マップ（反射）— 光沢球(MeshPhysical)の質感向上。
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environmentIntensity = 0.5; // 反射の白被りを抑える

  const camera = new THREE.PerspectiveCamera(
    45,
    window.innerWidth / window.innerHeight,
    0.01,
    100,
  );
  camera.up.set(0, 0, 1); // z-up

  const ambient = new THREE.AmbientLight(0xffffff, 0.3);
  scene.add(ambient);
  const key = new THREE.DirectionalLight(0xffffff, 1.45);
  key.position.set(1.2, -1.5, 3);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.camera.near = 0.5;
  key.shadow.camera.far = 8;
  scene.add(key);

  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  });

  // iOS Safari（16.4+/17/18）はバックグラウンド化・ロック解除で WebGL コンテキストを
  // 失うことがある（既知の WebKit バグ）。preventDefault しないとブラウザは復帰イベントを
  // 出さず黒画面のまま固定される。preventDefault すると three.js が次フレームで GPU 資源を
  // 再構築するため描画が復帰する。コンテキスト喪失時のみ発火する純粋な防御ハンドラで、
  // 正常時の描画挙動には影響しない。
  renderer.domElement.addEventListener(
    'webglcontextlost',
    (event) => {
      event.preventDefault();
      console.warn('WebGL コンテキストを喪失。復帰を待機します（iOS Safari 等）。');
    },
    false,
  );

  return { renderer, scene, camera };
};
